import { useState, useEffect } from 'react';
import { Shield, HelpCircle } from 'lucide-react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';

// 隐私政策内容
const privacyContent = [
  {
    id: 1,
    title: '隐私政策概述',
    content: `1.1 「活动精英助手」（手机 App 端名称）与「活动精英网」（网页端名称）均由郑州捕风捉影文化传播有限公司（以下简称"我们"或"运营方"）开发与运营，是同一套活动订单预约与对账核算管理系统（对外亦称"活动精英订单预约管理系统"）的不同终端形态，以下统称"本平台"。本隐私政策说明我们如何收集、使用、存储、共享和保护您的个人信息，以及您可行使的权利。

1.2 本平台面向活动执行者、自由职业人员及中小企业，是订单预约与对账核算的管理工具。本平台不代收代付、不参与订单定价、不担保交易、不保管您的交易资金或资金凭据；会员费由持牌第三方支付机构收取，本平台不自行归集资金。

1.3 请在使用本平台前仔细阅读本隐私政策。一旦您注册账户或使用本平台的任何功能，即表示您已阅读并同意本隐私政策。

1.4 本政策所称"个人信息"，指以电子或者其他方式记录的与已识别或者可识别的自然人有关的各种信息，不包括匿名化处理后的信息。您的身份证件号码属于敏感个人信息，我们在第 6 章单独说明其处理规则；对敏感个人信息的处理，我们在采集前逐项单独告知并取得您的同意。`
  },
  {
    id: 2,
    title: '我们收集的信息',
    content: `2.1 注册与登录信息：用户名、电子邮箱、手机号、登录密码（仅存储不可逆的哈希值，我们不留存明文），以及用于登录校验的验证码与登录凭据。

2.2 实名认证信息：为落实"用户即客户、系统内身份唯一"，注册时我们要求您提供真实姓名、身份证件号码与身份证照片，用于身份一致性核验，采集与存储方式详见第 6 章。

2.3 个人资料信息：您主动填写的头像、昵称、地区、地址、紧急联系人及其电话、个人简介等。

2.4 业务数据：您创建的订单、订单项、金额、付款与结算记录、备注、人员调用（支出订单）、对账与分享记录，以及您名下的客户档案与自定义类型、标签。

2.5 使用信息：为保障账号安全，我们记录登录时间、来源 IP 与设备／系统类型，以及关键操作的留痕日志。

2.6 设备与推送信息：为把消息通知送达您的设备，我们需要设备推送令牌、设备标识、机型与系统版本、网络状态，具体范围与用途见第 3 章。

2.7 会员费支付信息：您购买会员时，我们与第三方支付机构之间传递平台订单号、金额与支付结果状态。我们不留存您的银行卡号、支付密码、验证码或完整账户凭据。

2.8 除上述信息外，我们不主动收集与本平台服务目的无关的个人信息，也不采集您的通讯录、短信内容、通话记录、设备位置信息或已安装应用列表。`
  },
  {
    id: 3,
    title: '设备信息的收集范围与用途',
    content: `3.1 收集目的：为把订单审核、档期催办等消息通知在 App 未打开时也能送达您的设备，我们需要获取必要的设备与网络信息。这是您能实时收到业务提醒所必需的信息。

3.2 收集范围：
• 设备推送令牌（registration_id / push token）：由手机厂商推送服务生成，用于将通知投递到本机。
• 设备标识：App 被系统回收、无法维持网络连接时，仍需通过厂商系统长连接把通知送达设备，因此需要识别设备的唯一性。需要向您说明的是，设备标识并非只由推送 SDK 采集：您发起会员费支付时，支付宝收银台 SDK 也会采集设备标识，且安卓端与鸿蒙端采集的内容不同（安卓端为系统的 Android ID，鸿蒙端为其内置 UTDID 组件生成的设备标识），见第 5 章 5.6。您选择微信付款时，微信 OpenSDK 按其官方规则处理设备型号信息，用途限于完成本次支付，见第 5 章 5.7。
• 设备型号与系统版本：用于适配不同手机系统的推送与支付能力，判断该设备能否接收通知、能否拉起收银台。
• 网络类型：判断当前网络环境是否允许接收推送与完成支付通信。

3.3 使用限制：上述信息仅用于"消息送达"与"支付风控"这两项用途，不会用于建立用户画像、不会用于精准营销，也不会与您的业务数据关联后用于其他目的。

3.4 不采集的范围：我们不会收集您的通讯录、短信内容、通话记录、设备位置信息或已安装应用列表，也不会采集设备的 IMEI、IMSI、硬件序列号或网卡 MAC 地址，本 App 未为此申请电话权限。相册仅在您主动选择上传图片时读取您所选中的那几张，不读取整个相册。

3.5 采集时机：上述设备信息由手机厂商推送 SDK 与支付宝 SDK 在您同意本政策后初始化时采集；在您同意之前，App 不会初始化这些 SDK，因此不会产生上述采集。`
  },
  {
    id: 4,
    title: '权限使用说明',
    content: `4.1 最小必要原则：本 App 仅在您主动使用相关功能时才申请对应权限，不会在启动时预先索取。

4.2 本 App 申请的权限与用途（安卓侧以打包后实际生效的权限清单为准，与本机实测一致）：
• 网络访问与网络状态（INTERNET、ACCESS_NETWORK_STATE、ACCESS_WIFI_STATE）：与本平台服务器通信、判断当前网络环境。
• 相机（CAMERA）：仅在您点击「拍照」上传身份证照片、点击头像拍照，或使用「扫码登录」时申请，用于拍摄与扫描，不在后台开启。
• 读取图片（READ_MEDIA_IMAGES；Android 9 及以下系统对应为外置存储读写权限）：仅在您主动选择从相册上传图片时读取您所选中的那几张。
• 通知（POST_NOTIFICATIONS）：仅在您开启消息推送时申请，用于 App 未打开时送达提醒。
• 振动（VIBRATE）：消息提醒与操作反馈。
• 厂商推送自有权限（OPPO、vivo、小米、荣耀各自的推送通道权限）：由厂商推送 SDK 声明，仅用于维持推送长连接与在系统内投递通知，不用于读取您的任何数据。
• 应用内部广播权限（com.huodongjingying.booking.DYNAMIC_RECEIVER_NOT_EXPORTED_PERMISSION）：AndroidX 框架自带的签名级内部权限，只用于本 App 内部注册消息接收器，不涉及您的任何数据。

4.3 本 App 不申请的权限：录音（麦克风）、位置（精确定位与粗略定位）、读取通讯录、读取短信与通话记录、读取已安装应用列表、日历、身体传感器。您可在系统「设置 - 应用管理 - 权限」中查看本 App 已获得的全部权限。

4.4 拒绝的后果与撤回：您拒绝某项授权不影响其他功能使用，仅对应功能不可用（例如拒绝相机则无法拍照上传与扫码登录）；您可在系统「设置 - 应用管理 - 权限」中随时撤回。

4.5 剪贴板：本 App 仅在您点击「复制」时，把邀请链接、客户联系方式等内容写入您设备的剪贴板；我们不读取剪贴板内容，也未申请剪贴板权限。

4.6 鸿蒙端（按上架包实测的权限清单，共 4 条）：
• 网络访问（ohos.permission.INTERNET）：与本平台服务器通信、与支付通道通信。
• 获取网络信息（ohos.permission.GET_NETWORK_INFO）：判断当前网络连接状态，用于推送与支付前的可用性判断。
• 隐私窗口（ohos.permission.PRIVACY_WINDOW）：仅在支付页面开启防截屏／防录屏与多任务缩略图隐藏，是保护支付信息的措施，不用于采集任何信息。
• 持久化存储（ohos.permission.STORE_PERSISTENT_DATA）：供收银台 SDK 在本机留存其设备标识，使同一设备的标识保持稳定。
本 App 自身只声明第 1 条，后三条由支付宝鸿蒙收银台 SDK 与其内置的 UTDID 组件声明、打包时合并进上架包。鸿蒙端不申请相机、相册、麦克风、位置、通讯录、短信、通话记录、日历或身体传感器权限：拍照与选图使用系统相机和系统图片选择器，扫码使用系统 Scan Kit，均在应用之外由系统完成。`
  },
  {
    id: 5,
    title: '第三方 SDK 与服务商清单',
    content: `本章逐条列出随 App 分发的第三方 SDK 与由我们服务器调用的服务商，每条给出提供方全称、处理的信息、用途与官方条款原文链接。以下推送通道只存在于安卓端安装包，且按您手机的品牌生效；鸿蒙端使用系统内置的推送服务与扫码能力，不集成这些开发包。

5.1 华为推送 SDK——华为软件技术有限公司：处理应用基本信息；设备型号、操作系统信息、系统设置信息；网络信息、运营商信息、SSID、IP 地址。官方声明载明：系统设置信息与运营商信息、SSID、IP 地址仅在本地处理，不上传华为服务器。用途：在 App 未打开时送达订单与审核提醒。仅在您同意本政策后初始化。官方条款《SDK隐私声明》：https://developer.huawei.com/consumer/cn/doc/HMSCore-Guides/sdk-data-security-0000001050042177

5.2 荣耀推送 SDK——深圳荣耀软件技术有限公司：处理设备标识符（AAID、PushToken）、APPID、应用包名。用途与初始化时机同上。官方条款《SDK隐私声明》：https://developer.honor.com/cn/docs/11002/guides/sdk-data-security

5.3 OPPO PUSH SDK——广东欢太科技有限公司：处理 RegisterId、appKey、appSecret、应用包名、应用版本号、推送 SDK 版本号。用途与初始化时机同上。官方条款《OPPO PUSH SDK个人信息保护政策》：https://open.oppomobile.com/new/developmentDoc/info?id=11228

5.4 vivo 推送 SDK——维沃移动通信有限公司：处理设备标识信息（ANDROIDID、OAID、GAID、VAID、RegID 等；安卓 9 及以下为 IMEI）、应用包名与版本号、APPID、运行状态、设备制造商、设备型号与操作系统版本、网络信息、国家码与设备类型、通知栏与锁屏状态。用途与初始化时机同上。官方条款《vivo推送隐私政策》：https://dev.vivo.com.cn/documentCenter/doc/652

5.5 小米推送 SDK——北京小米移动软件有限公司：处理应用包名、版本号与运行状态；设备厂商、设备型号、设备内存、操作系统版本、小米推送 SDK 版本、设备归属地、SIM 卡运营商名称、当前网络类型、WiFi 状态（官方政策载明：当前网络类型与运营商名称仅在设备本地读取）；通知栏设置。用途与初始化时机同上。官方条款《小米推送隐私政策》：https://dev.mi.com/console/doc/detail?pId=1822

5.6 支付宝支付 SDK：用于在 App 内拉起支付宝收银台完成会员费支付。款项由支付宝（持牌支付机构）收取，本平台不经手、不归集、不保管您的资金。它采集的内容随设备系统不同：
• 安卓端（alipaysdk-android）：读取系统提供的 Android ID（用于生成支付风控标识）、设备型号与厂商等机型信息、当前网络状态，以及本次支付的订单号与金额。
• 鸿蒙端（@cashier_alipay/cashiersdk 收银台）：除订单与金额信息外，其内置的 UTDID 组件会在您的设备本机生成一个应用级设备标识（不含硬件序列号，不能跨应用关联），并把该标识与应用包名上报至阿里巴巴的设备标识服务（audid-api.taobao.com）；另含蓝盾（blueshield）安全组件，用于支付运行环境的风险校验。这两个组件是收银台 SDK 的必要依赖，随其一并分发，无法单独摘除。
• 采集时机：上述采集发生在您点击进入会员购买、收银台被拉起之时；不使用会员购买功能即不会触发。
• 使用限制：这些标识仅用于支付风控与收银台会话保持，不参与本平台的业务数据关联，也不用于营销画像。
官方条款《App 支付宝客户端 SDK 隐私说明》（同一份说明覆盖安卓与鸿蒙平台）：https://opendocs.alipay.com/open/06834t

5.7 微信 OpenSDK：用于唤起微信客户端完成会员费支付（安卓端为经 fluwx 桥接的 com.tencent.mm.opensdk，鸿蒙端为 @tencent/wechat_open_sdk）。按其官方《微信 Open SDK 个人信息处理规则》，它处理的个人信息为微信支付订单标识、支付是否成功的状态信息与设备型号信息；本 App 不通过它获取您的微信号、头像或昵称。
• 另需说明：经在本 App 两端安装包内实测核对，该 SDK 自带支付过程遥测模块，包内存在上报地址 https://log.wechatpay.cn/mmpayendpointreport/opensdkreport ，用于支付链路的质量监控。鸿蒙版按其 1.0.23 版源码逐项核对，上报字段为系统版本、接口级别、AppID、prepay_id、商户号、结果码与耗时等支付过程信息；安卓端以包内字符串实测为据，具体字段范围以官方规则为准。该上报由 SDK 在您的设备上直接发起，本平台不参与、也不接收这些数据，接收方为境内主体。
• 采集时机与使用限制：仅在您点击进入会员购买、拉起微信支付时发生；不使用会员购买功能即不会触发。接入它没有为本 App 新增任何权限。
款项由财付通（微信支付的持牌收款机构）收取，本平台不经手、不归集、不保管您的资金。官方条款《微信 Open SDK 个人信息处理规则》：https://support.weixin.qq.com/cgi-bin/mmsupportacctnodeweb-bin/pages/RYiYJkLOrQwu0nb8 ；《财付通隐私政策》：https://posts.tenpay.com/posts/18ed0968618e3db204d4931651708953.html

5.8 条码识别组件（开源库 zxing-cpp，Apache-2.0 许可）：用于「扫码登录」的二维码识别。它在您的手机本机离线完成识别，不联网、不上传图像、不收集任何个人信息；它是开源代码而非个人信息处理者，故没有面向终端用户的隐私政策，此处给出源码地址供核对：https://github.com/zxing-cpp/zxing-cpp 。鸿蒙端的扫一扫使用系统 Scan Kit，不集成该组件。

5.9 以下三项由我们的服务器调用，不在您的设备内安装 SDK：短信与邮件服务商（收集手机号或邮箱，用途为发送验证码与业务通知）、实名信息核验服务商（收集姓名与身份证件信息，用途为注册环节的身份一致性核验）、地图与地理编码服务（收集您手动填写的活动地址文本，用途为把地址换算为可导航的坐标；本 App 不采集设备位置信息）。

5.10 上述 5.1—5.8 是随 App 一起分发、在您设备上运行的组件；5.9 由我们的服务器调用，不在您的设备内安装 SDK。

5.11 我们只向上述组件与服务商交付完成该事项所必需的最少信息；它们各自处理信息的规则以其官方隐私政策为准，本章已逐条给出原文链接。除本章列明的范围外，本 App 未集成其他用于收集个人信息的第三方 SDK。手机 App 端另有同一份清单，可在 App 内「设置 → 隐私 → 第三方信息共享清单」查阅。`
  },
  {
    id: 6,
    title: '实名认证信息的特别处理',
    content: `6.1 收集目的：本平台内的订单基于真实的用户／客户身份而存在，错误的身份将导致错误的订单归属与核算，故需要在注册环节进行身份认证。

6.2 验证时点：身份验证仅在注册环节进行，验证通过后不在日常使用中重复采集。

6.3 存储方式：您的身份证件号码不以明文落库。我们留存的是它的密文（AES-256-GCM 加密）、用于唯一性比对的哈希值（HMAC-SHA256），以及列表展示用的掩码（仅显示前 6 位与后 4 位）；核验服务商返回的原始报文按字段脱敏后才留痕。身份证照片保存在本平台自有服务器，不交付第三方云存储。

6.4 访问控制：身份证件号码的密文只在授权场景（如管理员审核注册申请）解密，且解密后的展示仍然脱敏；每一次对敏感字段的访问都会留痕，留痕包含访问人与被访问字段，可事后追溯。

6.5 身份唯一与合并：已认证身份高于系统内未认证身份；对重复身份，我们将未认证的用户／客户身份向已认证的对应身份合并或清理，并保留必要的合并留痕。

6.6 注销删除：您注销账号时，关键身份信息随注销一并删除，法律法规要求留存的除外。`
  },
  {
    id: 7,
    title: '信息使用方式',
    content: `7.1 提供服务：使用相关信息为您提供订单管理、预约调度、客户管理、对账核算等本平台的各项功能。

7.2 账号与安全：用于身份验证、登录设备管理、防止欺诈与滥用，保障账号与数据安全。

7.3 消息通知：向您发送与服务相关的通知，如审核提醒、档期催办、结算与系统消息等。

7.4 改进服务：在合法、必要的范围内分析使用情况，以改进平台功能与服务质量。

7.5 我们不会将您的个人信息用于上述目的之外的商业用途，也不会出售您的个人信息。`
  },
  {
    id: 8,
    title: '跨用户可见与信息共享',
    content: `8.1 交易对方的可见性：本平台以"订单"连接交易双方。您创建的订单，其约定的对方（客户／交易对象）在与该订单相关的范围内可见相应内容（如订单信息、金额、状态与审核记录），以便双方对账核对。这是平台正常功能所必需，不构成越权使用。

8.2 人员调用（支出订单）的可见性：涉及人员调用的订单，被调用人员在其相关范围内可见必要的订单信息。

8.3 第三方服务商：在必要情况下，我们会委托第三方服务商处理信息，并要求其按约定保护您的信息安全。目前实际接入的通道是：短信与邮件服务商（发送验证码与服务通知）、实名信息核验服务商（仅用于注册环节的身份一致性核验）、地图与地理编码服务（把您填写的活动地址换算为导航坐标）、手机厂商消息推送通道（华为、荣耀、OPPO、vivo、小米，用于 App 未打开时送达订单与审核提醒）、支付宝与财付通（即微信支付的收款机构，两家均为持牌支付机构，用于收取会员费）。我们只向上述服务商交付完成该事项所必需的最少信息；本平台不自行归集或保管用户资金；订单附件与图片保存在本平台自有服务器，不交付第三方云存储。各通道在您的设备上是否运行 SDK，以第 5 章的区分口径为准。另需说明：鸿蒙端收银台 SDK 会就其设备标识自行向阿里巴巴的设备标识服务发起上报（详见 5.6），支付宝与微信的支付组件还会各自向本方日志服务上报支付过程信息（微信一侧的地址与字段见 5.7）；这些上报均由 SDK 在您的设备上直接发起，本平台不参与、也不接收该数据；接收方均为境内主体。

8.4 法律要求：根据法律法规、有权机关的强制性要求，我们可能会提供相应信息。

8.5 除上述情形外，未经您的同意，我们不会向第三方共享您的个人信息。`
  },
  {
    id: 9,
    title: '信息存储与保护',
    content: `9.1 存储地点：您的信息存储在我们管理的位于中华人民共和国境内的服务器上。本平台的数据中心与服务器均位于境内，不向境外提供您的个人信息，也不使用境外服务器或境外 SDK。

9.2 保护措施：我们采取加密存储、传输加密（HTTPS/TLS）、访问控制、凭证脱敏与操作留痕等措施保护您的信息，防止未经授权的访问、泄露、篡改或丢失；但请理解互联网环境不存在绝对安全。

9.3 存储期限：本平台不按时间自动删除或归档您的业务数据，没有"到期清空"机制；订单等为软删除，记录在满足对账与法定留存要求期间予以保留。

9.4 注销与删除：您注销账号后，我们删除或匿名化处理您的关键个人信息，法律法规要求留存的除外。

9.5 安全事件处置：如发生个人信息泄露、篡改、丢失等安全事件，我们将按法律法规要求及时告知您事件情况与处置措施，并向监管部门报告。`
  },
  {
    id: 10,
    title: 'Cookie、登录会话与本地存储',
    content: `10.1 为保障您持续登录与操作安全，本平台使用必要的会话凭据（含 HttpOnly 的登录令牌）在设备与服务器之间维持登录状态；这类凭据仅用于身份识别与会话续期。

10.2 您可通过退出登录或清除浏览器数据移除本地会话信息；退出后相关令牌将被吊销。

10.3 App 端的本地存储：登录凭据保存在系统提供的加密存储中（安卓侧由系统密钥库保护），界面偏好与草稿保存在本应用私有目录，其他应用无法读取；您在 App 内退出登录后，这些凭据即被清除。`
  },
  {
    id: 11,
    title: '您的权利',
    content: `11.1 访问与更正：您有权在"个人中心"查看、访问并更正、更新您的个人资料信息。

11.2 导出备份：您可在"设置 → 数据与隐私"导出本人账本的数据备份（尊享会员功能），以便自行留存与迁移交接。

11.3 删除：您可通过取消订单、删除资料等方式处置您的业务数据；订单不能由用户直接删除，只能取消，彻底删除需联系客服。

11.4 撤回同意与注销：您可撤回对信息收集和使用的同意或申请注销账号，但这可能影响您使用相关服务功能。撤回同意不影响撤回前基于您同意已进行的处理活动。

11.5 约束信息系统自动决策：本平台不作对个人权益有重大影响的自动化决策，也不提供基于用户画像的定向推送。

11.6 行使权利：如需行使上述权利或对本政策有疑问，请通过"联系我们"与我们取得联系，我们将在 7 个工作日内回复。`
  },
  {
    id: 12,
    title: '未成年人保护',
    content: `12.1 本平台主要面向具有完全民事行为能力的用户，不面向未成年人提供注册与交易管理服务。

12.2 我们已在注册验证环节限制了未成年人的注册，但无法完全保证验证的准确性；我们如发现未经核实的孩子信息，将第一时间删除清理，相关订单也将一并清除。`
  },
  {
    id: 13,
    title: '政策更新与联系我们',
    content: `13.1 本政策最近更新日期为 2026 年 10 月 16 日。

13.2 政策更新：我们可能不时更新本隐私政策，更新后的政策将在本页面公布并同步更新上述日期；涉及新增采集项或变更使用目的等重大变更时，我们会在 App 内以弹窗或公告方式再次提示并征得您的同意。继续使用本平台表示您同意更新后的隐私政策。

13.3 联系我们：如您对本隐私政策有任何疑问、意见或行权请求，欢迎通过平台"联系我们"页面与我们取得联系，我们将在 7 个工作日内回复。`
  }
];

/**
 * 正文里的第三方官方条款链接要能点开：按 URL 切成段，命中段渲染成 <a>，其余原样输出。
 * 段末紧跟的中文标点与全角符号不并入链接，免得把「。」吃进 href。
 */
function renderPolicyText(text: string) {
  return text
    .split(/(https?:\/\/[^\s，。；、（）【】《》「」]+)/g)
    .map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-teal-600 underline underline-offset-2 hover:text-teal-700"
        >
          {part}
        </a>
      ) : (
        <span key={i}>{part}</span>
      )
    );
}

export default function PrivacyPage() {
  const { siteName, logoUrl } = useSiteConfig();
  const [isScrolled, setIsScrolled] = useState(false);

  // 监听滚动事件，控制导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 顶部导航 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-10 h-10"
            />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a href="/contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              联系我们
            </a>
            <a 
              href="/login" 
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* Hero 顶部区域 */}
      <section className="pt-[140px] pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-teal-800">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          {/* 标签徽章 */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/20 border border-teal-400/30 mb-6">
            <Shield className="h-4 w-4 text-teal-300" />
            <span className="text-xs font-medium text-teal-300">法律文件</span>
          </div>

          {/* 标题 */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            隐私政策
          </h1>

          {/* 描述文字 */}
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 leading-relaxed">
            我们致力于保护您的隐私，本政策说明了我们如何收集、使用和保护您的个人信息。
          </p>
        </div>
      </section>

      {/* 主内容区 */}
      <main className="flex-1 py-14">
        <div className="max-w-md md:max-w-2xl mx-auto px-6">
          {/* 面包屑导航 */}
          <div className="text-xs text-slate-500 mb-8">
            <a href="/" className="hover:text-teal-600 transition-colors">首页</a>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-semibold">隐私政策</span>
          </div>

          {/* 目录卡片 */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 mb-10">
            <h2 className="text-sm font-semibold text-slate-500 tracking-wider uppercase mb-4">
              目录
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {privacyContent.map((item) => (
                <a 
                  key={item.id}
                  href={`#section-${item.id}`}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition-colors py-1"
                >
                  <span className="w-5 h-5 bg-teal-50 text-teal-600 rounded text-xs flex items-center justify-center">
                    {item.id}
                  </span>
                  {item.title}
                </a>
              ))}
            </div>
          </div>

          {/* 条款内容卡片 */}
          {privacyContent.map((item) => (
            <div key={item.id} id={`section-${item.id}`} className="bg-white rounded-xl border border-slate-100 p-7 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 bg-teal-500 text-white rounded-lg text-xs font-bold flex items-center justify-center">
                  {item.id}
                </span>
                <h3 className="text-base font-bold text-slate-800">
                  {item.title}
                </h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {renderPolicyText(item.content)}
              </p>
            </div>
          ))}

          {/* 底部提示卡片 */}
          <div className="bg-teal-50 border border-teal-100 rounded-xl p-6 text-center mt-10">
            <HelpCircle className="h-5 w-5 text-teal-500 mx-auto mb-2 block" />
            <p className="text-sm text-teal-700 leading-relaxed">
              如您对本隐私政策有任何疑问，欢迎
              <a href="/contact" className="font-medium underline hover:text-teal-900 cursor-pointer mx-1">
                联系我们
              </a>
              ，我们将在 7 个工作日内回复。
            </p>
          </div>
        </div>
      </main>

      {/* 底部导航 */}
      <footer className="bg-slate-800 text-white py-8">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* 左侧：Logo和品牌信息 */}
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto"
              />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>

            {/* 右侧：导航链接 */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>

          {/* 底部备案信息 */}
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>
    </div>
  );
}
